import * as React from 'react';
import {MediaAssetImageModel} from '../../../generated/core';

import {VideoPlayerV2VideoProps} from '../../d6/components/video-player-v2';
import {MediaAspectRatio, Scene7VideoRendition} from '../../../generated/api';
import {useVideoSource} from './hooks/useVideoSource';
import {DefaultVideoPlayerProxy} from '../../services/video/VideoPlayerProxy';
import {VideoPoster} from '../videoPoster';
import {VideoWithFocalPoint} from '../../d6/components/video-with-focal-point';
import {FocalPoint} from '../../d6/components/focal-point';
import {ResponsiveMediaRendererConf} from '../ResponsiveMediaRenderer';
import {useAudioDescription} from './hooks/useAudioDescription';
import {useFeatureToggles, useLocaleModel} from '../../context';

const noop = () => undefined;

export const DEFAULT_VIDEO_FOCAL_POINT: FocalPoint = {x: 'center', y: 'center'};

export interface VideoComponentFactoryProps {
    videoRenditions: Scene7VideoRendition[];
    videoPlayerProxy: React.RefObject<DefaultVideoPlayerProxy>;
    videoContainerRef: React.RefObject<HTMLDivElement>;
    coverImageModel: MediaAssetImageModel;
    aspectRatio?: MediaAspectRatio;
    playsInLoop?: boolean;
    scene7FileReference?: string;
    subtitlesFileReference?: string;
    audioDescriptionFileReference?: string;
    title?: string;
    muted?: boolean;
    responsiveMediaConfig?: ResponsiveMediaRendererConf[];
    onVideoDurationChange?(e: React.SyntheticEvent<HTMLVideoElement>): void;
    onCoverImageLoad?: () => void;
    onCoverImageError?: () => void;
}

export function VideoComponent(
    props: VideoPlayerV2VideoProps & VideoComponentFactoryProps
): JSX.Element {
    const {
        onPlay,
        innerRef,
        onDurationChange,
        onPause,
        onEnded,
        onTimeUpdate,
        onLoadedMetadata,
        muted: propsMuted,
        keepOriginalAspectRatio,
        videoRenditions,
        aspectRatio,
        coverImageModel,
        playsInLoop,
        subtitlesFileReference,
        audioDescriptionFileReference,
        title = '',
        muted: factoryMuted,
        onVideoDurationChange = noop, // function coming from videoComponentFactoryProps
        onCoverImageLoad,
        onCoverImageError,
        videoPlayerProxy,
        videoContainerRef,
        responsiveMediaConfig,
        isAudioDescriptionEnabled
    } = props;

    const {language, countryCode} = useLocaleModel();
    const langCountryCode = `${language}-${countryCode}`;
    const {enableAudioDescriptionFeat} = useFeatureToggles();

    const audioDescTrackRef = React.useRef<HTMLTrackElement | null>(null);
    const audioDescTrack = audioDescTrackRef.current;

    const muted = factoryMuted || propsMuted;
    const videoElement = innerRef.current;
    const videoSource = useVideoSource(
        videoRenditions,
        aspectRatio,
        videoPlayerProxy,
        videoContainerRef
    );

    const coverImage = React.useMemo(
        () => (
            <VideoPoster
                image={coverImageModel}
                onLoad={onCoverImageLoad}
                onError={onCoverImageError}
                responsiveMediaConfig={responsiveMediaConfig}
            />
        ),
        [
            coverImageModel,
            onCoverImageError,
            onCoverImageLoad,
            responsiveMediaConfig
        ]
    );

    const [isCoverShown, setIsCoverShown] = React.useState(
        videoElement
            ? videoElement.paused &&
                  videoPlayerProxy.current &&
                  !videoPlayerProxy.current.isInFullScreen
            : true
    );

    const handlePlay = React.useCallback(() => {
        onPlay();
        setIsCoverShown(false);
    }, [onPlay]);

    React.useEffect(() => {
        // just to be safe
        if (isCoverShown && videoElement && !videoElement.paused) {
            setIsCoverShown(false);
        }
    }, [isCoverShown, videoElement]);

    const onDurationChangeCallback = React.useCallback(
        (e: React.SyntheticEvent<HTMLVideoElement>) => {
            onVideoDurationChange(e);
            onDurationChange();
        },
        [onVideoDurationChange, onDurationChange]
    );

    const isAudioDescFullyEnabled =
        enableAudioDescriptionFeat && isAudioDescriptionEnabled;

    useAudioDescription({
        audioDescTrack,
        video: videoElement,
        langCountryCode,
        isEnabled: isAudioDescFullyEnabled
    });

    return (
        <>
            <VideoWithFocalPoint
                innerRef={innerRef}
                focalPoint={DEFAULT_VIDEO_FOCAL_POINT}
                onPlay={handlePlay}
                onPause={onPause}
                onEnded={onEnded}
                onTimeUpdate={onTimeUpdate}
                onLoadedMetadata={onLoadedMetadata}
                onDurationChange={onDurationChangeCallback}
                muted={muted}
                loop={playsInLoop}
                aria-label={title}
                controls={false}
                playsInline
                src={videoSource}
                preload="metadata"
                keepOriginalAspectRatio={keepOriginalAspectRatio}
            >
                {subtitlesFileReference && (
                    <track
                        default
                        kind="subtitles"
                        src={subtitlesFileReference}
                    />
                )}
                {enableAudioDescriptionFeat &&
                    audioDescriptionFileReference && (
                        <track
                            ref={audioDescTrackRef}
                            // According to MDN the default attribute "may only be used on one track element per media element". However, this causes the track files to not be downloaded at all. Therefore we kept both "default" attributes for now.
                            default
                            kind="metadata"
                            src={audioDescriptionFileReference}
                            srcLang={language}
                        />
                    )}
            </VideoWithFocalPoint>
            {isCoverShown && coverImage}
        </>
    );
}
